import { useEffect, useState } from 'react';
import { plateformesAPI } from 'services/swagger-api';
import {
  GetCurrentPlateformeResponse,
} from 'api/plateforme';
import { Dropdown, DropdownItem } from '../forms/Dropdown';

export const PlateformeSwitcher = () => {
  const [plateformeData, setPlateformeData] = useState<GetCurrentPlateformeResponse | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<DropdownItem<string | number> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiLogs, setApiLogs] = useState<Array<{ timestamp: string; type: string; data: any }>>([]);

  // Fonction pour logger les appels API
  const logApiCall = (type: string, data: any) => {
    const timestamp = new Date().toLocaleTimeString();
    setApiLogs(prev => [...prev, { timestamp, type, data }]);
  };

  // Charger les plateformes au montage
  useEffect(() => {
    loadPlateformes();
  }, []);

  const loadPlateformes = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('📡 API Call: GET /api/backoffice/plateformes/current');
      logApiCall('GET /current', 'Fetching current plateforme...');

      const response = await plateformesAPI.getCurrentPlateforme();

      console.log('✅ API Response:', response);
      logApiCall('GET /current - SUCCESS', response);

      setPlateformeData(response);

      // Définir la plateforme courante comme sélectionnée
      if (response.currentPlateforme) {
        setSelectedOrg({
          key: response.currentPlateforme.id,
          label: response.currentPlateforme.name
        });
      }
    } catch (err: any) {
      console.error('❌ API Error:', err);
      logApiCall('GET /current - ERROR', {
        message: err.message,
        status: err.status,
        body: err.body
      });
      setError(err.message || 'Erreur lors du chargement des plateformes');
    } finally {
      setLoading(false);
    }
  };

  const handlePlateformeSwitch = async (item: DropdownItem<string | number>) => {
    try {
      await plateformesAPI.switchCurrentPlateforme({
        plateformeId: String(item.key)
      });

      // Rechargement sans vider les storages : garde la mémorisation du choix
      // de plateforme et le jeton de l'onglet.
      window.location.replace('/');

    } catch (err) {
      console.error('❌ Erreur:', err);
    }
  }

  // Transformer les plateformes en items pour le dropdown
  const dropdownItems: DropdownItem<string | number>[] = plateformeData?.plateformes.map(org => ({
    key: org.id,
    label: org.name
  })) || [];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">🔄 Plateforme Switcher - Test Component</h1>

      {/* Section de sélection */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Sélectionner une plateforme</h2>

        <div className="max-w-md">
          <Dropdown
            label="Plateforme"
            name="plateforme"
            items={dropdownItems}
            selectedItem={selectedOrg}
            onSelect={handlePlateformeSwitch}
            placeholder="Choisir une plateforme..."
            disabled={loading}
          />
        </div>

        {loading && (
          <div className="mt-4 text-blue-600 font-medium">
            ⏳ Chargement en cours...
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
            ❌ {error}
          </div>
        )}
      </div>

      {/* Affichage des données actuelles */}
      {plateformeData && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">📊 Données actuelles</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Plateforme courante */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-3 text-green-600">
                ✅ Plateforme Courante
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="font-medium">ID:</span>{' '}
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                    {plateformeData.currentPlateforme.id}
                  </code>
                </div>
                <div>
                  <span className="font-medium">Nom:</span>{' '}
                  <span className="text-lg">{plateformeData.currentPlateforme.name}</span>
                </div>
              </div>
            </div>

            {/* Thème */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-3">🎨 Thème</h3>
              <div className="space-y-2">
                <div>
                  <span className="font-medium">Couleur primaire:</span>{' '}
                  <span
                    className="inline-block w-6 h-6 rounded border border-gray-300 ml-2 align-middle"
                    style={{ backgroundColor: plateformeData.theme.primaryColor }}
                  ></span>
                  <code className="ml-2 bg-gray-100 px-2 py-1 rounded text-sm">
                    {plateformeData.theme.primaryColor}
                  </code>
                </div>
                <div>
                  <span className="font-medium">Couleur secondaire:</span>{' '}
                  <span
                    className="inline-block w-6 h-6 rounded border border-gray-300 ml-2 align-middle"
                    style={{ backgroundColor: plateformeData.theme.secondaryColor }}
                  ></span>
                  <code className="ml-2 bg-gray-100 px-2 py-1 rounded text-sm">
                    {plateformeData.theme.secondaryColor}
                  </code>
                </div>
              </div>
            </div>
          </div>

          {/* Liste des plateformes disponibles */}
          <div className="mt-6 border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3">📋 Plateformes disponibles ({plateformeData.plateformes.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {plateformeData.plateformes.map(org => (
                <div
                  key={org.id}
                  className={`p-3 rounded border ${org.id === plateformeData.currentPlateforme.id
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-gray-50'
                    }`}
                >
                  <div className="font-medium">{org.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {org.id}
                  </div>
                  {org.id === plateformeData.currentPlateforme.id && (
                    <div className="text-xs text-green-600 font-semibold mt-1">
                      ✓ Active
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Logs API */}
      <div className="bg-gray-900 rounded-lg shadow-md p-6 text-gray-100">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">🔍 Logs API</h2>
          <button
            onClick={() => setApiLogs([])}
            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm"
          >
            Effacer les logs
          </button>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {apiLogs.length === 0 ? (
            <div className="text-gray-400 italic">Aucun appel API pour le moment...</div>
          ) : (
            apiLogs.map((log, index) => (
              <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-400">{log.timestamp}</span>
                  <span className={`text-sm font-semibold ${log.type.includes('ERROR') ? 'text-red-400' :
                    log.type.includes('SUCCESS') ? 'text-green-400' : 'text-blue-400'
                    }`}>
                    {log.type}
                  </span>
                </div>
                <pre className="text-xs bg-gray-800 p-3 rounded overflow-x-auto">
                  {JSON.stringify(log.data, null, 2)}
                </pre>
              </div>
            ))
          )}
        </div>
      </div>

      {/* JSON brut complet */}
      {plateformeData && (
        <div className="mt-6 bg-gray-50 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">📝 JSON Brut (Réponse complète)</h2>
          <pre className="bg-white p-4 rounded border border-gray-200 overflow-x-auto text-sm">
            {JSON.stringify(plateformeData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};
